import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from './AuthContext'
import { getMe, type BrandAccessWire, type MeWire } from '../api/client'
import { safeStorage } from './storage'

/**
 * Who the user is and what they may do, from the server rather than from the token.
 *
 * The Blazor client base64-decoded the JWT in the browser to read `str_perm` and
 * `brand_str_perm`, and carried a ~40-key legacy role map as a fallback. That does not survive the
 * move to Keycloak — Keycloak tokens will not carry Relay's 58 permissions, and putting them there
 * would inflate a token that also travels in a WebSocket URL. So `/api/me` is the single authority,
 * cached by React Query, and the legacy map dies with the old client rather than being re-typed.
 */

// The wire shapes live with the client that fetches them. Every request this portal makes is built
// in `src/api/client.ts` — a rule the route ratchet enforces rather than assumes — and `src/api`
// must not depend on the shell, so the types moved there rather than being imported backwards.

export interface Brand {
  id: string
  name: string
  roleName: string
}

export interface UserProfile {
  id: string
  email: string
  displayName: string
  brands: Brand[]
}

/**
 * Map the legacy per-brand booleans onto Relay's permission keys.
 *
 * Only used when the server does not yet return `permissions`. It is deliberately a narrow
 * translation of the five flags `/api/me` actually returns — not a re-implementation of the old
 * client's 40-key map, which guessed at keys the server never sent.
 */
export function derivePermissions(access: BrandAccessWire[]): string[] {
  const flags: Array<[keyof NonNullable<BrandAccessWire['permissions']>, string[]]> = [
    ['can_view_devices', ['device.view']],
    ['can_connect_vnc', ['device.connect.vnc']],
    ['can_connect_ssh', ['device.connect.ssh']],
    ['can_manage_devices', ['device.manage']],
    // As the server maps it (PermissionService): the flag alone grants no user administration.
    ['can_manage_users', ['user.manage.permissions']],
  ]
  const derived = new Set<string>()
  for (const entry of access) {
    for (const [flag, keys] of flags) {
      if (entry.permissions?.[flag]) keys.forEach((k) => derived.add(k))
    }
  }
  return [...derived].sort()
}

export interface PermissionsContextValue {
  user: UserProfile | null
  brands: Brand[]
  /**
   * The brand the operator is working in, or `null` for every brand they can see.
   *
   * Read through `useActiveBrand()` rather than here: that hook is what the list pages use, and it
   * is the one place that decides how the switcher combines with a page's own brand filter.
   */
  activeBrandId: string | null
  /** `null` selects "All brands". */
  setActiveBrand: (brandId: string | null) => void
  permissions: string[]
  hasPermission: (key: string) => boolean
  /**
   * Held globally or on a whole brand, not only at some stores. A brand's configuration (profiles, groups,
   * rollouts, software, provisioning) is kept from a user limited to some stores; the pages for it gate on this.
   */
  hasBrandWidePermission: (key: string) => boolean
  isLoading: boolean
  error: Error | null
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null)

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, accessToken } = useAuth()
  // The choice made in this session, tagged with whose it is, so a different user signing in on
  // the same tab starts from their own remembered choice rather than the last user's.
  const [chosen, setChosen] = useState<{ userId: string; brandId: string | null } | null>(null)

  const {
    data: me,
    isLoading,
    error,
  } = useQuery<MeWire>({
    queryKey: ['me'],
    enabled: isAuthenticated && !!accessToken,
    queryFn: () => getMe(accessToken!),
    staleTime: 5 * 60 * 1000,
  })

  // Memoised for the same reason `permissions` below is: as a bare `??` it is a new array every
  // render, which would make the memo that depends on it recompute every render anyway.
  const access = useMemo(() => me?.brand_access ?? [], [me])
  const brands: Brand[] = access.map((entry) => ({
    id: entry.brand_id,
    name: entry.brand_name,
    roleName: entry.role_name,
  }))

  const user: UserProfile | null = me
    ? { id: me.id, email: me.email, displayName: me.name ?? me.email, brands }
    : null

  // `/api/me`: one authority for what an operator may do — the identity provider's token carries
  // none of it. The five per-brand booleans remain as the last resort for a relay that sends no
  // `permissions`, which cannot express `system.admin` and so is sending very little.
  //
  // Memoised because `hasPermission` depends on it: as a bare logical expression this is a fresh
  // array on every render, so every consumer of the context would see a new callback each time —
  // the same defect eslint caught on the telemetry page's `connections ?? []`.
  const permissions = useMemo(
    () => me?.permissions ?? (me ? derivePermissions(access) : []),
    [me, access],
  )

  // The active brand: this session's choice, else the one remembered for this user, else "All
  // brands". Whatever it is must still be one of the user's brands — access is revoked, brands are
  // removed — and with one brand or none there is nothing to choose, so it is always "All".
  //
  // Keyed on a joined string rather than on `brands`, which is a fresh array on every render.
  const userId = me?.id ?? null
  const brandIds = access.map((entry) => entry.brand_id).join(',')
  const activeBrandId = useMemo(() => {
    if (!userId) return null
    const ids = brandIds === '' ? [] : brandIds.split(',')
    if (ids.length <= 1) return null
    const wanted = chosen?.userId === userId ? chosen.brandId : readRememberedBrand(userId)
    return wanted && ids.includes(wanted) ? wanted : null
  }, [userId, brandIds, chosen])

  const setActiveBrand = useCallback(
    (id: string | null) => {
      if (!userId) return
      setChosen({ userId, brandId: id })
      rememberBrand(userId, id)
    },
    [userId],
  )

  const hasPermission = useCallback(
    (key: string) => {
      // `*` is the platform's wildcard, and system.admin is Relay's master override — the same
      // override PermissionService applies server-side. Default deny for everything else.
      if (permissions.includes('*') || permissions.includes('system.admin')) return true
      return permissions.includes(key)
    },
    [permissions],
  )

  // From a relay that predates store-limited access, everything held is held brand-wide.
  const brandWide = me?.brand_wide_permissions
  const hasBrandWidePermission = useCallback(
    (key: string) => {
      if (!brandWide) return hasPermission(key)
      return brandWide.includes('system.admin') || brandWide.includes(key)
    },
    [brandWide, hasPermission],
  )

  return (
    <PermissionsContext.Provider
      value={{
        user,
        brands,
        activeBrandId,
        setActiveBrand,
        permissions,
        hasPermission,
        hasBrandWidePermission,
        isLoading,
        error: (error as Error) ?? null,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  )
}

const BRAND_KEY_PREFIX = 'relay-active-brand:'

/** The brand this user last chose on this browser, or `null` for "All brands" or nothing stored. */
function readRememberedBrand(userId: string): string | null {
  try {
    return safeStorage()?.getItem(BRAND_KEY_PREFIX + userId) || null
  } catch {
    return null
  }
}

/** Best effort: a store that refuses the write leaves the choice in force for this session only. */
function rememberBrand(userId: string, brandId: string | null) {
  try {
    const store = safeStorage()
    if (!store) return
    if (brandId) store.setItem(BRAND_KEY_PREFIX + userId, brandId)
    else store.removeItem(BRAND_KEY_PREFIX + userId)
  } catch {
    // Private mode or blocked storage.
  }
}

export function usePermissions(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext)
  if (!ctx) throw new Error('usePermissions must be used within PermissionsProvider')
  return ctx
}
